/**
 * ComponenteDocumentosSugeridos
 * 
 * CONTEXTO DE NEGÓCIO:
 * Este componente exibe a lista de documentos sugeridos pela análise automática da LLM
 * após o upload da petição inicial (TAREFA-042). Cada documento é exibido em um card
 * com informações sobre sua importância (prioridade) e justificativa técnica.
 * 
 * O usuário pode fazer upload de cada documento individualmente ou marcar como "Não Possuo".
 * A validação garante que todos os documentos ESSENCIAIS sejam enviados ou justificados.
 * 
 * FUNCIONALIDADES:
 * - Exibição de documentos sugeridos em cards visuais
 * - Badges de prioridade com cores distintas (ESSENCIAL = vermelho, IMPORTANTE = amarelo, DESEJAVEL = verde)
 * - Upload individual por documento com barra de progresso
 * - Suporte a múltiplos uploads simultâneos
 * - Validação de documentos ESSENCIAIS antes de avançar
 * - Integração com sistema de upload assíncrono (TAREFA-036)
 * 
 * INTEGRAÇÃO:
 * - Recebe lista de documentos sugeridos da análise da petição
 * - Chama POST /api/peticoes/{peticao_id}/documentos para upload
 * - Faz polling de status de cada upload via GET /api/documentos/status-upload/{upload_id}
 * - Atualiza estado no componente pai quando todos uploads completarem
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDropzone, type DropzoneOptions, type FileRejection } from 'react-dropzone';
import type { 
  DocumentoSugerido, 
  PrioridadeDocumento,
  StatusUploadDocumento 
} from '../../tipos/tiposPeticao';
import { 
  uploadDocumentosComplementares,
  verificarStatusUpload 
} from '../../servicos/servicoApiPeticoes';
import { AlertCircle, CheckCircle, Upload, XCircle, FileText, Loader } from 'lucide-react';
import { useModal } from '../../../../core/contextos/modalContext';

/**
 * Informação de documento já enviado anteriormente
 */
export interface DocumentoJaEnviado {
  /** ID do documento no backend */
  documento_id: string;
  
  /** Nome original do arquivo */
  nome_arquivo: string;
  
  /** Tipo de documento sugerido associado (se conhecido) */
  tipo_documento?: string;
  
  /** Status atual do upload (CONCLUIDO, PROCESSANDO, etc) */
  status_upload: string;
  
  /** Progresso do processamento (0-100) */
  progresso_percentual?: number;
}

import type { ArquivoIgnorado } from './ComponenteUploadPeticaoInicial';

/**
 * Propriedades do componente ComponenteDocumentosSugeridos
 */
interface PropriedadesComponenteDocumentosSugeridos {
  /** ID da petição à qual os documentos serão associados */
  peticaoId: string;
  
  /** Lista de documentos sugeridos retornada pela análise da LLM */
  documentosSugeridos: DocumentoSugerido[];
  
  /** 
   * Lista de documentos já enviados anteriormente (para restaurar estado ao retornar à página)
   * CORREÇÃO CRÍTICA: Permite que o componente reconheça uploads realizados anteriormente
   */
  documentosJaEnviados?: DocumentoJaEnviado[];
  
  /**
   * Lista de arquivos ignorados durante o upload inicial (formatos não suportados)
   */
  arquivosIgnorados?: ArquivoIgnorado[];
  
  /** Callback chamado quando todos os documentos necessários forem processados */
  aoCompletarDocumentos: (documentosEnviados: string[]) => void;
  
  /** Callback chamado em caso de erro durante o upload */
  aoOcorrerErro: (mensagemErro: string) => void;
}

/**
 * Estado interno de cada documento para controle de upload
 */
interface EstadoDocumento {
  /** Documento sugerido original */
  documentoSugerido: DocumentoSugerido & { manual?: boolean };
  
  /** Status atual do upload (NÃO_ENVIADO | SELECIONANDO | ENVIANDO | PROCESSANDO | CONCLUIDO | ERRO | MARCADO_NAO_POSSUO) */
  status: StatusUploadDocumento;
  
  /** IDs dos uploads retornados pelo backend (UUID) - um por arquivo */
  uploadIds: string[];
  
  /** IDs dos documentos processados (retornados quando status = CONCLUIDO) - um por arquivo */
  documentoIds: string[];
  
  /** Progresso do upload/processamento (0-100) - médio dos arquivos */
  progressoPercentual: number;
  
  /** Etapa atual do processamento (ex: "Extraindo texto", "Vetorizando") */
  etapaAtual: string | null;
  
  /** Mensagem de erro (se status = ERRO) */
  mensagemErro: string | null;
  
  /** Arquivos selecionados pelo usuário */
  arquivos: File[];
  
  /** Status de cada arquivo individual */
  statusesArquivos: StatusUploadDocumento[];
  
  /** Progresso de cada arquivo */
  progressoArquivos: number[];
  
  /** Etapas de cada arquivo */
  etapasArquivos: (string | null)[];
  
  /** Intervalo de polling (para cleanup) */
  intervalId: NodeJS.Timeout | null;

  /** Instruções opcionais fornecidas pelo usuário para o analista */
  instrucoes?: string;

  /** Indica se o documento foi criado manualmente pelo usuário */
  manual?: boolean;
}

/**
 * Componente de Exibição de Documentos Sugeridos
 * 
 * Este é o segundo passo do wizard de análise de petição inicial (TAREFA-049).
 * Após o upload da petição e análise dos documentos relevantes (TAREFA-042),
 * este componente permite ao usuário fazer upload dos documentos disponíveis.
 */
export function ComponenteDocumentosSugeridos(
  props: PropriedadesComponenteDocumentosSugeridos
): JSX.Element {
  const { peticaoId, documentosSugeridos, documentosJaEnviados, arquivosIgnorados, aoCompletarDocumentos, aoOcorrerErro } = props;

  // Hook para controlar modais de alerta/confirmação
  const { mostrarConfirmacao } = useModal();

  // Estado: Lista de documentos com estado de upload individual
  const [estadosDocumentos, setEstadosDocumentos] = useState<EstadoDocumento[]>([]);

  // Estado: Indica se o botão "Avançar" está habilitado
  const [podeAvancar, setPodeAvancar] = useState<boolean>(false);

  // Ref para armazenar intervalos de polling (para cleanup)
  const intervalosPendentesRef = useRef<Set<NodeJS.Timeout>>(new Set());

  /**
   * Helper para verificar se um status indica conclusão do upload
   * Aceita múltiplos formatos: CONCLUIDO, concluido, completed, done, etc.
   */
  const isStatusConcluido = useCallback((status: string) => {
    const statusNormalizado = (status || '').toLowerCase();
    return statusNormalizado === 'concluido' || 
           statusNormalizado === 'completed' || 
           statusNormalizado === 'done' ||
           statusNormalizado === 'finalizado' ||
           statusNormalizado === 'processado' ||
           statusNormalizado.includes('conclu') ||
           statusNormalizado.includes('complet');
  }, []);

  /**
   * Inicializa o estado de cada documento sugerido quando o componente monta
   * ou quando a lista de documentos sugeridos muda.
   * 
   * CORREÇÃO CRÍTICA: Agora também verifica se há documentos já enviados
   * anteriormente e marca-os como CONCLUIDO para preservar o estado ao retornar.
   */
  useEffect(() => {
    // Criar um Set com documento_ids já utilizados para evitar duplicação
    const idsUtilizados = new Set<string>();
    
    const estadosIniciais: EstadoDocumento[] = documentosSugeridos.map(doc => {
      // CORREÇÃO CRÍTICA: Verificar se este tipo de documento já foi enviado
      // Prioriza match por tipo_documento (quando disponível do backend), depois por nome do arquivo
      const documentoEnviadoRelacionado = documentosJaEnviados?.find(enviado => {
        // Pular se já foi utilizado para outro documento sugerido
        if (idsUtilizados.has(enviado.documento_id)) return false;
        
        // Verificar se o status indica que foi concluído
        if (!isStatusConcluido(enviado.status_upload)) return false;
        
        const tipoNormalizado = doc.tipo_documento.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        
        // MATCH PRIORITÁRIO: Se o backend retornou tipo_documento, usar para match direto
        if (enviado.tipo_documento) {
          const tipoEnviadoNormalizado = enviado.tipo_documento.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          // Match exato ou parcial com tipo_documento
          if (tipoNormalizado === tipoEnviadoNormalizado || 
              tipoNormalizado.includes(tipoEnviadoNormalizado) ||
              tipoEnviadoNormalizado.includes(tipoNormalizado)) {
            return true;
          }
        }
        
        // MATCH FALLBACK: Tenta match por nome do arquivo (mantém compatibilidade)
        const nomeArquivoNormalizado = enviado.nome_arquivo.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        
        // Match 1: Verifica palavras-chave do tipo de documento (mínimo 2 chars)
        const palavrasChave = tipoNormalizado.split(/\s+/).filter(p => p.length > 2);
        const contemPalavraChave = palavrasChave.some(palavra => nomeArquivoNormalizado.includes(palavra));
        
        // Match 2: Verifica se nome do arquivo contém alguma parte do tipo
        const tipoSemEspacos = tipoNormalizado.replace(/\s+/g, '');
        const contemTipo = nomeArquivoNormalizado.includes(tipoSemEspacos) || 
                          tipoSemEspacos.includes(nomeArquivoNormalizado.replace(/\.[^.]+$/, ''));
        
        // Match 3: Match por primeiras palavras significativas
        const primeiraPalavra = palavrasChave[0];
        const contemPrimeiraPalavra = primeiraPalavra && nomeArquivoNormalizado.includes(primeiraPalavra);
        
        return contemPalavraChave || contemTipo || contemPrimeiraPalavra;
      });
      
      // Se encontrou documento já enviado relacionado, marca como CONCLUIDO
      if (documentoEnviadoRelacionado) {
        // Marcar como utilizado
        idsUtilizados.add(documentoEnviadoRelacionado.documento_id);
        return {
          documentoSugerido: doc,
          status: 'CONCLUIDO' as StatusUploadDocumento,
          uploadIds: [],
          documentoIds: [documentoEnviadoRelacionado.documento_id],
          progressoPercentual: 100,
          etapaAtual: 'Concluído anteriormente',
          mensagemErro: null,
          arquivos: [],
          statusesArquivos: ['CONCLUIDO' as StatusUploadDocumento],
          progressoArquivos: [100],
          etapasArquivos: ['Concluído'],
          intervalId: null
        };
      }
      
      // Documento ainda não enviado
      return {
        documentoSugerido: doc,
        status: 'NAO_ENVIADO' as StatusUploadDocumento,
        uploadIds: [],
        documentoIds: [],
        progressoPercentual: 0,
        etapaAtual: null,
        mensagemErro: null,
        arquivos: [],
        statusesArquivos: [],
        progressoArquivos: [],
        etapasArquivos: [],
        intervalId: null
      };
    });

    setEstadosDocumentos(estadosIniciais);
    
    // Log para debug quando há documentos já enviados
    if (documentosJaEnviados && documentosJaEnviados.length > 0) {
      const concluidos = estadosIniciais.filter(e => e.status === 'CONCLUIDO').length;
      console.log(`[ComponenteDocumentosSugeridos] Restaurados ${concluidos} documentos já enviados de ${documentosJaEnviados.length} disponíveis`);
      
      // Log detalhado para debug se não conseguiu restaurar
      if (concluidos === 0) {
        console.log('[ComponenteDocumentosSugeridos] Debug - Documentos sugeridos:', documentosSugeridos.map(d => d.tipo_documento));
        console.log('[ComponenteDocumentosSugeridos] Debug - Documentos já enviados:', documentosJaEnviados.map(d => ({ nome: d.nome_arquivo, tipo: d.tipo_documento, status: d.status_upload })));
      }
    }
  }, [documentosSugeridos, documentosJaEnviados, isStatusConcluido]);

  /**
   * Valida se o usuário pode avançar para a próxima etapa
   * 
   * REGRAS DE VALIDAÇÃO (ATUALIZADAS):
   * 1. Todos os documentos ESSENCIAIS devem estar CONCLUIDOS ou MARCADO_NAO_POSSUO
   *    OU deve haver documentos já enviados anteriormente (carregados do backend)
   * 2. Pelo menos 1 documento deve estar CONCLUIDO (enviado com sucesso)
   *    OU deve haver documentos já enviados anteriormente
   * 3. Nenhum documento deve estar em status de upload (ENVIANDO ou PROCESSANDO)
   */
  useEffect(() => {
    // Verificar se há documentos
    if (estadosDocumentos.length === 0) {
      setPodeAvancar(false);
      return;
    }

    // Verificar se há documentos já enviados anteriormente (mesmo que não associados)
    // Usa o helper isStatusConcluido para aceitar múltiplos formatos de status
    const temDocumentosJaEnviados = !!(documentosJaEnviados && documentosJaEnviados.length > 0 &&
      documentosJaEnviados.some(d => isStatusConcluido(d.status_upload)));

    // REGRA 1: Todos ESSENCIAIS devem estar processados ou marcados como "Não Possuo"
    const todosEssenciaisProcessados = estadosDocumentos
      .filter(estado => estado.documentoSugerido.prioridade === 'essencial')
      .every(estado => 
        estado.status === 'CONCLUIDO' || 
        estado.status === 'MARCADO_NAO_POSSUO'
      );

    // REGRA 2: Pelo menos 1 documento foi enviado com sucesso
    const peloMenosUmEnviado = estadosDocumentos.some(estado => 
      estado.status === 'CONCLUIDO'
    ) || temDocumentosJaEnviados;

    // REGRA 3: Nenhum documento em upload (ENVIANDO ou PROCESSANDO)
    const nenhumEmUpload = estadosDocumentos.every(estado => 
      estado.status !== 'ENVIANDO' && estado.status !== 'PROCESSANDO'
    );

    // Se tem documentos já enviados do backend, permitir avançar mesmo sem match perfeito
    // (o usuário já fez uploads anteriores)
    const podeAvancarComDocsAnteriores = temDocumentosJaEnviados && nenhumEmUpload;
    
    setPodeAvancar((todosEssenciaisProcessados && peloMenosUmEnviado && nenhumEmUpload) || podeAvancarComDocsAnteriores);
  }, [estadosDocumentos, documentosJaEnviados, isStatusConcluido]);

  /**
   * Cleanup: Limpa todos os intervalos de polling quando o componente desmonta
   */
  useEffect(() => {
    const intervalos = intervalosPendentesRef.current;
    return () => {
      // Limpar todos os intervalos pendentes para prevenir memory leaks
      intervalos.forEach(intervalo => clearInterval(intervalo));
      intervalos.clear();
    };
  }, []);

  /**
  /**
   * Busca informações do documento processado após upload completo
   * 
   * Endpoint: GET /api/documentos/resultado-upload/{upload_id}
   * 
   * @param indice - Índice do documento no array estadosDocumentos
   * @param uploadId - ID do upload retornado pelo backend
   * @param indiceArquivo - Índice do arquivo dentro do documento
   */
  const buscarInformacoesDocumentoProcessado = useCallback(async (
    indice: number,
    uploadId: string,
    indiceArquivo: number
  ) => {
    try {
      // Importar função de API para obter resultado do upload
      const { obterResultadoUpload } = await import('../../servicos/servicoApiPeticoes');
      
      const resposta = await obterResultadoUpload(uploadId);
      const { documento_id } = resposta.data;

      // Atualizar estado: armazenar documento_id para o arquivo específico
      setEstadosDocumentos(estadosAnteriores => {
        const novosEstados = [...estadosAnteriores];
        const estadoAtual = novosEstados[indice];
        const novosDocumentoIds = [...estadoAtual.documentoIds];
        novosDocumentoIds[indiceArquivo] = documento_id;
        
        novosEstados[indice] = {
          ...estadoAtual,
          documentoIds: novosDocumentoIds
        };
        return novosEstados;
      });

    } catch (erro: unknown) {
      const error = erro as { response?: { data?: { detail?: string } }; message?: string };
      console.error(`Erro ao buscar informações do documento processado (upload ${uploadId}):`, error);
      // Não bloquear o fluxo se falhar buscar informações adicionais
    }
  }, []);

  /**
   * Inicia polling para verificar status do upload de um documento
   * 
   * POLLING:
   * - Intervalo: 2 segundos
   * - Timeout: 30 minutos (1800 segundos)
   * - Endpoint: GET /api/documentos/status-upload/{upload_id}
   * 
   * @param indice - Índice do documento no array estadosDocumentos
   * @param uploadId - ID do upload retornado pelo backend
   * @param indiceArquivo - Índice do arquivo dentro do documento
   */
  const iniciarPollingUpload = useCallback((
    indice: number,
    uploadId: string,
    indiceArquivo: number
  ) => {
    let tentativas = 0;
    const maxTentativas = 900; // 900 * 2s = 1800s = 30min

    const intervalo = setInterval(async () => {
      tentativas++;

      try {
        // Verificar status do upload
        const resposta = await verificarStatusUpload(uploadId);
        const { status, etapa_atual, progresso_percentual, mensagem_erro } = resposta.data;

        // Atualizar estado com progresso para o arquivo específico
        setEstadosDocumentos(estadosAnteriores => {
          const novosEstados = [...estadosAnteriores];
          const estadoAtual = novosEstados[indice];
          
          // CORREÇÃO: Garantir que os arrays tenham tamanho suficiente antes de atualizar
          // Isso evita que índices fora do range causem problemas
          const tamanhoNecessario = Math.max(
            indiceArquivo + 1, 
            estadoAtual.statusesArquivos.length,
            estadoAtual.progressoArquivos.length,
            estadoAtual.etapasArquivos.length
          );
          
          // Criar arrays com tamanho garantido
          const novosStatusesArquivos = [...estadoAtual.statusesArquivos];
          const novosProgressosArquivos = [...estadoAtual.progressoArquivos];
          const novasEtapasArquivos = [...estadoAtual.etapasArquivos];
          
          // Preencher até o tamanho necessário
          while (novosStatusesArquivos.length < tamanhoNecessario) {
            novosStatusesArquivos.push('PROCESSANDO' as StatusUploadDocumento);
          }
          while (novosProgressosArquivos.length < tamanhoNecessario) {
            novosProgressosArquivos.push(0);
          }
          while (novasEtapasArquivos.length < tamanhoNecessario) {
            novasEtapasArquivos.push(null);
          }
          
          // Atualizar status do arquivo específico
          novosStatusesArquivos[indiceArquivo] = status as StatusUploadDocumento;
          novosProgressosArquivos[indiceArquivo] = progresso_percentual || 0;
          novasEtapasArquivos[indiceArquivo] = etapa_atual || null;
          
          // CORREÇÃO: Calcular progresso médio apenas para arquivos válidos (excluir zeros de placeholder)
          const progressosValidos = novosProgressosArquivos.filter((p, i) => i < estadoAtual.arquivos.length);
          const progressoMedio = progressosValidos.length > 0 
            ? progressosValidos.reduce((a, b) => a + b, 0) / progressosValidos.length 
            : 0;
          
          // CORREÇÃO: Determinar status geral considerando apenas arquivos reais
          const statusesReais = novosStatusesArquivos.slice(0, estadoAtual.arquivos.length);
          let statusGeral: StatusUploadDocumento = 'PROCESSANDO';
          
          // Se todos os arquivos reais estão concluídos, marcar como concluído
          if (statusesReais.length > 0 && statusesReais.every(s => s === 'CONCLUIDO')) {
            statusGeral = 'CONCLUIDO';
          } else if (statusesReais.some(s => s === 'ERRO')) {
            statusGeral = 'ERRO';
          }
          
          novosEstados[indice] = {
            ...estadoAtual,
            statusesArquivos: novosStatusesArquivos,
            progressoArquivos: novosProgressosArquivos,
            etapasArquivos: novasEtapasArquivos,
            status: statusGeral,
            progressoPercentual: progressoMedio,
            etapaAtual: etapa_atual || estadoAtual.etapaAtual,
            mensagemErro: mensagem_erro || estadoAtual.mensagemErro
          };
          return novosEstados;
        });

        // Se status = CONCLUIDO ou ERRO, parar polling
        if (status === 'CONCLUIDO' || status === 'ERRO') {
          clearInterval(intervalo);
          intervalosPendentesRef.current.delete(intervalo);

          // Se CONCLUIDO, buscar informações do documento processado
          if (status === 'CONCLUIDO') {
            buscarInformacoesDocumentoProcessado(indice, uploadId, indiceArquivo);
          }

          return;
        }

      } catch (erro: unknown) {
        const error = erro as { response?: { data?: { detail?: string } }; message?: string };
        console.error(`Erro ao verificar status do upload ${uploadId}:`, error);

        // Se atingiu timeout, parar polling e marcar como erro
        if (tentativas >= maxTentativas) {
          clearInterval(intervalo);
          intervalosPendentesRef.current.delete(intervalo);

          setEstadosDocumentos(estadosAnteriores => {
            const novosEstados = [...estadosAnteriores];
            const estadoAtual = novosEstados[indice];
            
            const novosStatusesArquivos = [...estadoAtual.statusesArquivos];
            novosStatusesArquivos[indiceArquivo] = 'ERRO';
            
            // Recalcular status geral
            let statusGeral: StatusUploadDocumento = 'PROCESSANDO';
            if (novosStatusesArquivos.every(s => s === 'CONCLUIDO')) {
              statusGeral = 'CONCLUIDO';
            } else if (novosStatusesArquivos.some(s => s === 'ERRO')) {
              statusGeral = 'ERRO';
            }
            
            novosEstados[indice] = {
              ...estadoAtual,
              statusesArquivos: novosStatusesArquivos,
              status: statusGeral,
              mensagemErro: 'Timeout: Upload demorou mais de 30 minutos',
              progressoPercentual: 0,
              etapaAtual: null
            };
            return novosEstados;
          });

          aoOcorrerErro('Timeout: Upload demorou mais de 30 minutos');
        }
      }
    }, 2000); // Polling a cada 2 segundos

    // Armazenar intervalo para cleanup
    intervalosPendentesRef.current.add(intervalo);

    // Timeout de segurança: se não receber CONCLUIDO em 30min, cancelar
    setTimeout(() => {
      if (intervalosPendentesRef.current.has(intervalo)) {
        clearInterval(intervalo);
        intervalosPendentesRef.current.delete(intervalo);

        setEstadosDocumentos(estadosAnteriores => {
          const novosEstados = [...estadosAnteriores];
          if (novosEstados[indice].status !== 'CONCLUIDO' && novosEstados[indice].status !== 'ERRO') {
            novosEstados[indice] = {
              ...novosEstados[indice],
              status: 'ERRO',
              mensagemErro: 'Timeout: Upload demorou mais de 30 minutos',
              progressoPercentual: 0,
              etapaAtual: null
            };
          }
          return novosEstados;
        });
      }
    }, 1800000); // 30 minutos
  }, [aoOcorrerErro, buscarInformacoesDocumentoProcessado]);

  /**
   * Inicia o upload de documentos para o backend
   * 
   * FLUXO:
   * 1. Chama POST /api/peticoes/{peticao_id}/documentos (TAREFA-043)
   * 2. Recebe upload_ids imediatamente (202 Accepted)
   * 3. Inicia polling de status para cada upload
   * 4. Atualiza UI com progresso em tempo real
   * 
   * @param indice - Índice do documento no array estadosDocumentos
   * @param arquivos - Arquivos selecionados pelo usuário
   */
  const iniciarUploadDocumento = useCallback(async (
    indice: number,
    arquivos: File[]
  ) => {
    try {
      // Atualizar status: ENVIANDO
      setEstadosDocumentos(estadosAnteriores => {
        const novosEstados = [...estadosAnteriores];
        novosEstados[indice] = {
          ...novosEstados[indice],
          status: 'ENVIANDO',
          progressoPercentual: 0,
          etapaAtual: 'Iniciando upload...'
        };
        return novosEstados;
      });

      // Preparar dados para upload (incluindo categoria = tipo_documento para re-hidratação)
      const instrucoes = estadosDocumentos[indice]?.instrucoes;
      const categoria = estadosDocumentos[indice]?.documentoSugerido?.tipo_documento;
      const dadosUpload = arquivos.map(arquivo => ({ arquivo, instrucoes, categoria }));

      // Chamar API: POST /api/peticoes/{peticao_id}/documentos
      const resposta = await uploadDocumentosComplementares(peticaoId, dadosUpload);

      // Resposta esperada: { documentos_enviados: [{ upload_id, documento_id, ... }] }
      const documentosEnviados = resposta.data.documentos_enviados || [];
      
      if (documentosEnviados.length !== arquivos.length) {
        throw new Error('Número de uploads não corresponde ao número de arquivos');
      }

      // Coletar upload_ids
      const uploadIds = documentosEnviados.map((doc: { upload_id: string }) => doc.upload_id);

      // Atualizar estado: PROCESSANDO, armazenar upload_ids
      // CORREÇÃO: Capturar índice de início ANTES de iniciar polling para evitar race condition
      let indiceInicioCapturado: number = 0;
      
      setEstadosDocumentos(estadosAnteriores => {
        const novosEstados = [...estadosAnteriores];
        const estadoAtual = novosEstados[indice];
        
        // CORREÇÃO: Calcular índice com base nos uploadIds existentes (não nos arquivos)
        // Isso garante que o índice seja consistente mesmo se houver uploads anteriores
        indiceInicioCapturado = estadoAtual.uploadIds.length;
        
        // CORREÇÃO: Criar novos arrays para os novos uploads (não usar .map que apenas modifica)
        // Os novos arquivos já foram adicionados em handleDropArquivos, 
        // mas os arrays de status/progresso/etapas podem não ter o tamanho correto
        const tamanhoEsperado = estadoAtual.arquivos.length;
        
        // Garantir que os arrays de status/progresso/etapas tenham o tamanho correto
        let novosStatusesArquivos = [...estadoAtual.statusesArquivos];
        let novosProgressosArquivos = [...estadoAtual.progressoArquivos];
        let novasEtapasArquivos = [...estadoAtual.etapasArquivos];
        
        // Preencher arrays se estiverem menores que o esperado
        while (novosStatusesArquivos.length < tamanhoEsperado) {
          novosStatusesArquivos.push('PROCESSANDO' as StatusUploadDocumento);
        }
        while (novosProgressosArquivos.length < tamanhoEsperado) {
          novosProgressosArquivos.push(5);
        }
        while (novasEtapasArquivos.length < tamanhoEsperado) {
          novasEtapasArquivos.push('Upload iniciado');
        }
        
        // Atualizar os novos arquivos para PROCESSANDO (do índice de início em diante)
        for (let i = indiceInicioCapturado; i < novosStatusesArquivos.length; i++) {
          novosStatusesArquivos[i] = 'PROCESSANDO' as StatusUploadDocumento;
          novosProgressosArquivos[i] = 5;
          novasEtapasArquivos[i] = 'Upload iniciado';
        }
        
        // Calcular progresso médio considerando TODOS os arquivos
        const progressoMedio = novosProgressosArquivos.length > 0 
          ? novosProgressosArquivos.reduce((a, b) => a + b, 0) / novosProgressosArquivos.length 
          : 5;
        
        novosEstados[indice] = {
          ...estadoAtual,
          uploadIds: [...estadoAtual.uploadIds, ...uploadIds],
          status: 'PROCESSANDO',
          progressoPercentual: progressoMedio,
          etapaAtual: 'Upload iniciado',
          statusesArquivos: novosStatusesArquivos,
          progressoArquivos: novosProgressosArquivos,
          etapasArquivos: novasEtapasArquivos
        };
        return novosEstados;
      });

      // CORREÇÃO: Usar setTimeout para garantir que o estado foi atualizado antes de iniciar polling
      // Isso evita race condition onde o polling lê estado desatualizado
      setTimeout(() => {
        uploadIds.forEach((uploadId, idx) => {
          const indiceArquivo = indiceInicioCapturado + idx;
          iniciarPollingUpload(indice, uploadId, indiceArquivo);
        });
      }, 50);

    } catch (erro: unknown) {
      const error = erro as { response?: { data?: { detail?: string } }; message?: string };
      console.error(`Erro ao iniciar upload do documento (índice ${indice}):`, error);

      const mensagemErro = error.response?.data?.detail || 
                           error.message || 
                           'Erro desconhecido ao iniciar upload';

      // Atualizar estado: ERRO
      setEstadosDocumentos(estadosAnteriores => {
        const novosEstados = [...estadosAnteriores];
        novosEstados[indice] = {
          ...novosEstados[indice],
          status: 'ERRO',
          mensagemErro,
          progressoPercentual: 0,
          etapaAtual: null
        };
        return novosEstados;
      });

      aoOcorrerErro(mensagemErro);
    }
  }, [peticaoId, aoOcorrerErro, estadosDocumentos, iniciarPollingUpload]);

  /**
   * Handler: Atualiza instruções opcionais associadas a um documento
   *
   * @param indice - Índice do documento no array estadosDocumentos
   * @param valor - Texto digitado pelo usuário
   */
  const handleAtualizarInstrucoes = useCallback((indice: number, valor: string) => {
    setEstadosDocumentos(estadosAnteriores => {
      const novosEstados = [...estadosAnteriores];
      if (!novosEstados[indice]) {
        return novosEstados;
      }
      novosEstados[indice] = {
        ...novosEstados[indice],
        instrucoes: valor
      };
      return novosEstados;
    });
  }, []);

  /**
   * Handler: Quando o usuário marca o documento como "Não Possuo"
   * 
   * @param indice - Índice do documento no array estadosDocumentos
   */
  const handleMarcarNaoPossuo = useCallback((indice: number) => {
    const documento = estadosDocumentos[indice];

    // Se for ESSENCIAL, pedir confirmação
    if (documento.documentoSugerido.prioridade === 'essencial') {
      mostrarConfirmacao({
        titulo: 'Documento Essencial',
        mensagem: `O documento "${documento.documentoSugerido.tipo_documento}" é ESSENCIAL para a análise. ` +
          `Marcar como "Não Possuo" pode impactar a qualidade do resultado. Confirmar?`,
        aoConfirmar: () => {
          // Atualizar estado: MARCADO_NAO_POSSUO
          setEstadosDocumentos(estadosAnteriores => {
            const novosEstados = [...estadosAnteriores];
            novosEstados[indice] = {
              ...novosEstados[indice],
              status: 'MARCADO_NAO_POSSUO',
              progressoPercentual: 100,
              etapaAtual: 'Marcado como não disponível'
            };
            return novosEstados;
          });
        },
      });
      return;
    }

    // Atualizar estado: MARCADO_NAO_POSSUO (para não essenciais, sem confirmação)
    setEstadosDocumentos(estadosAnteriores => {
      const novosEstados = [...estadosAnteriores];
      novosEstados[indice] = {
        ...novosEstados[indice],
        status: 'MARCADO_NAO_POSSUO',
        progressoPercentual: 100,
        etapaAtual: 'Marcado como não disponível'
      };
      return novosEstados;
    });
  }, [estadosDocumentos, mostrarConfirmacao]);

  /**
   * Handler: Quando o usuário arrasta e solta arquivos em um card de documento (drag & drop)
   * 
   * @param indice - Índice do documento no array estadosDocumentos
   * @param arquivosAceitos - Arquivos aceitos pelo dropzone
   * @param arquivosRejeitados - Arquivos rejeitados (tipo ou tamanho inválido)
   */
  const handleDropArquivos = useCallback((
    indice: number,
    arquivosAceitos: File[],
    arquivosRejeitados: FileRejection[]
  ) => {
    // Se houve arquivos rejeitados, mostrar erro
    if (arquivosRejeitados.length > 0) {
      const erros = arquivosRejeitados.map(rejection => {
        const nomeArquivo = rejection.file.name;
        const errosArquivo = rejection.errors.map(e => {
          if (e.code === 'file-invalid-type') return 'tipo de arquivo inválido';
          if (e.code === 'file-too-large') return 'arquivo muito grande (máx. 20MB)';
          return e.message;
        }).join(', ');
        return `${nomeArquivo}: ${errosArquivo}`;
      });
      aoOcorrerErro(`Arquivos rejeitados:\n${erros.join('\n')}`);
    }

    // Se não há arquivos aceitos, não fazer nada
    if (arquivosAceitos.length === 0) {
      return;
    }

    // Atualizar estado: adicionar arquivos selecionados
    setEstadosDocumentos(estadosAnteriores => {
      const novosEstados = [...estadosAnteriores];
      const estadoAtual = novosEstados[indice];
      novosEstados[indice] = {
        ...estadoAtual,
        arquivos: [...estadoAtual.arquivos, ...arquivosAceitos],
        statusesArquivos: [
          ...estadoAtual.statusesArquivos,
          ...arquivosAceitos.map(() => 'SELECIONANDO' as StatusUploadDocumento)
        ],
        progressoArquivos: [
          ...estadoAtual.progressoArquivos,
          ...arquivosAceitos.map(() => 0)
        ],
        etapasArquivos: [
          ...estadoAtual.etapasArquivos,
          ...arquivosAceitos.map(() => null)
        ],
        status: 'SELECIONANDO'
      };
      return novosEstados;
    });

    // Iniciar upload para os novos arquivos
    iniciarUploadDocumento(indice, arquivosAceitos);
  }, [aoOcorrerErro, iniciarUploadDocumento]);

  /**
   * Handler: Quando o usuário clica em "Avançar"
   * 
   * Compila lista de IDs de documentos enviados com sucesso e
   * chama callback do componente pai.
   * 
   * CORREÇÃO: Também inclui documentos já enviados anteriormente que não
   * foram associados aos documentos sugeridos.
   */
  const handleAvancar = useCallback(() => {
    // Coletar IDs dos documentos enviados com sucesso (da sessão atual)
    const documentosEnviadosNaSessao = estadosDocumentos
      .flatMap(estado => estado.documentoIds)
      .filter(id => id !== null) as string[];

    // Coletar IDs dos documentos já enviados anteriormente (usa helper para aceitar múltiplos status)
    const documentosEnviadosAnteriormente = (documentosJaEnviados || [])
      .filter(doc => isStatusConcluido(doc.status_upload))
      .map(doc => doc.documento_id);

    // Combinar sem duplicatas
    const todosDocumentos = [...new Set([...documentosEnviadosNaSessao, ...documentosEnviadosAnteriormente])];
    
    console.log('[ComponenteDocumentosSugeridos] Avançando com documentos:', todosDocumentos);

    aoCompletarDocumentos(todosDocumentos);
  }, [estadosDocumentos, documentosJaEnviados, aoCompletarDocumentos, isStatusConcluido]);

  /**
   * Renderiza o badge de prioridade com cor apropriada
   * 
   * @param prioridade - essencial | importante | desejavel
   */
  const renderizarBadgePrioridade = (prioridade: PrioridadeDocumento): JSX.Element => {
    const configs = {
      essencial: {
        texto: 'ESSENCIAL',
        cor: 'bg-red-100 text-red-800 border-red-300'
      },
      importante: {
        texto: 'IMPORTANTE',
        cor: 'bg-yellow-100 text-yellow-800 border-yellow-300'
      },
      desejavel: {
        texto: 'DESEJÁVEL',
        cor: 'bg-green-100 text-green-800 border-green-300'
      }
    };

    const config = configs[prioridade];

    return (
      <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${config.cor}`}>
        {config.texto}
      </span>
    );
  };

  /**
   * Renderiza o ícone de status do documento
   * 
   * @param status - Status atual do documento
   */
  const renderizarIconeStatus = (status: StatusUploadDocumento): JSX.Element => {
    const icones = {
      NAO_ENVIADO: <FileText className="size-6 text-gray-400" />,
      SELECIONANDO: <Upload className="size-6 text-blue-500" />,
      ENVIANDO: <Loader className="size-6 animate-spin text-blue-500" />,
      PROCESSANDO: <Loader className="size-6 animate-spin text-blue-500" />,
      CONCLUIDO: <CheckCircle className="size-6 text-green-500" />,
      ERRO: <XCircle className="size-6 text-red-500" />,
      MARCADO_NAO_POSSUO: <AlertCircle className="size-6 text-gray-500" />
    };

    return icones[status] || icones.NAO_ENVIADO;
  };

  /**
   * Renderiza a barra de progresso do upload
   * 
   * @param estado - Estado do documento
   */
  const renderizarBarraProgresso = (estado: EstadoDocumento): JSX.Element | null => {
    if (estado.status === 'NAO_ENVIADO' || estado.status === 'MARCADO_NAO_POSSUO') {
      return null;
    }

    // CORREÇÃO: Calcular progresso real considerando apenas arquivos com status válido
    const arquivosProcessando = estado.statusesArquivos.filter(s => s === 'PROCESSANDO' || s === 'ENVIANDO');
    const arquivosConcluidos = estado.statusesArquivos.filter(s => s === 'CONCLUIDO');
    const totalArquivos = estado.arquivos.length;
    
    // CORREÇÃO: Calcular progresso médio apenas de arquivos válidos
    const progressoReal = totalArquivos > 0 
      ? Math.round(estado.progressoArquivos.slice(0, totalArquivos).reduce((a, b) => a + b, 0) / totalArquivos)
      : 0;
    
    // CORREÇÃO: Determinar etapa a exibir
    // Se há arquivos processando, mostrar a etapa do primeiro arquivo processando
    // Se todos concluídos, mostrar "Concluído"
    let etapaExibir = estado.etapaAtual || 'Processando...';
    
    if (arquivosProcessando.length > 0) {
      // Encontrar o índice do primeiro arquivo processando e mostrar sua etapa
      const indiceProcessando = estado.statusesArquivos.findIndex(s => s === 'PROCESSANDO' || s === 'ENVIANDO');
      if (indiceProcessando >= 0 && estado.etapasArquivos[indiceProcessando]) {
        etapaExibir = estado.etapasArquivos[indiceProcessando] || 'Processando...';
      }
      
      // Adicionar indicador de múltiplos arquivos se houver mais de um
      if (totalArquivos > 1) {
        etapaExibir = `${etapaExibir} (${arquivosConcluidos.length}/${totalArquivos} concluídos)`;
      }
    } else if (estado.status === 'CONCLUIDO') {
      etapaExibir = totalArquivos > 1 ? `Todos os ${totalArquivos} arquivos concluídos` : 'Concluído';
    }

    return (
      <div className="mt-3">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs text-gray-600">
            {etapaExibir}
          </span>
          <span className="text-xs font-semibold text-gray-600">
            {progressoReal}%
          </span>
        </div>
        <div className="h-2 w-full rounded-full bg-gray-200">
          <div
            className={`h-2 rounded-full transition-all duration-300 ease-in-out ${
              estado.status === 'CONCLUIDO' ? 'bg-green-600' : 'bg-blue-600'
            }`}
            style={{ width: `${progressoReal}%` }}
          />
        </div>
      </div>
    );
  };

  /**
   * Configuração do dropzone para aceitar arquivos
   */
  const dropzoneOptions: Omit<DropzoneOptions, 'onDrop'> = {
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png']
    },
    maxSize: 20 * 1024 * 1024, // 20MB
    multiple: true,
    noClick: false,
    noKeyboard: false
  };

  /**
   * Componente interno para card de documento com dropzone
   * Encapsula o useDropzone para cada card individualmente
   */
  const CardDocumentoComDropzone = ({ 
    estado, 
    indice 
  }: { 
    estado: EstadoDocumento; 
    indice: number;
  }) => {
    const podeReceberArquivos = estado.status === 'NAO_ENVIADO' || 
                                 estado.status === 'CONCLUIDO' || 
                                 estado.status === 'ERRO';
    
    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
      ...dropzoneOptions,
      onDrop: (arquivosAceitos, arquivosRejeitados) => {
        handleDropArquivos(indice, arquivosAceitos, arquivosRejeitados);
      },
      disabled: !podeReceberArquivos
    });

    // Classes condicionais para visual de drag and drop
    const getCardClasses = () => {
      const baseClasses = "relative rounded-lg border bg-white p-5 shadow-sm transition-all duration-200";
      
      if (!podeReceberArquivos) {
        return `${baseClasses} border-gray-300`;
      }
      
      if (isDragReject) {
        return `${baseClasses} border-2 border-dashed border-red-400 bg-red-50`;
      }
      
      if (isDragActive) {
        return `${baseClasses} border-2 border-dashed border-blue-400 bg-blue-50 scale-[1.01]`;
      }
      
      return `${baseClasses} border-gray-300 hover:shadow-md hover:border-blue-200`;
    };

    return (
      <div
        {...(podeReceberArquivos ? getRootProps() : {})}
        className={getCardClasses()}
      >
        {podeReceberArquivos && <input {...getInputProps()} />}
        
        {/* Overlay visual de drag and drop */}
        {isDragActive && podeReceberArquivos && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-blue-100/80">
            <div className="flex flex-col items-center space-y-2 text-blue-600">
              <Upload className="size-12 animate-bounce" />
              <span className="text-lg font-semibold">
                {isDragReject ? 'Tipo de arquivo não permitido' : 'Solte os arquivos aqui'}
              </span>
              <span className="text-sm">PDF, DOCX, JPEG, PNG (máx. 20MB)</span>
            </div>
          </div>
        )}

        <div className="mb-3 flex items-start justify-between">
          {/* Ícone de Status e Informações do Documento */}
          <div className="flex flex-1 items-start space-x-3">
            {renderizarIconeStatus(estado.status)}
            <div className="flex-1">
              {estado.manual ? (
                <>
                  <input
                    id="input-tipo-documento-manual"
                    type="text"
                    className="mb-1 w-full border-b border-gray-300 text-lg font-semibold text-gray-900 focus:border-primary-500 focus:outline-none"
                    placeholder="Tipo do documento (ex: RG, Certidão...)"
                    value={estado.documentoSugerido.tipo_documento}
                    maxLength={100}
                    onClick={(e) => e.stopPropagation()}
                    onChange={e => {
                      const valor = e.target.value;
                      setEstadosDocumentos(estadosAnteriores => {
                        const novosEstados = [...estadosAnteriores];
                        novosEstados[indice] = {
                          ...novosEstados[indice],
                          documentoSugerido: {
                            ...novosEstados[indice].documentoSugerido,
                            tipo_documento: valor
                          }
                        };
                        return novosEstados;
                      });
                    }}
                    required
                  />
                  <textarea
                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    rows={2}
                    placeholder="O que você quer que a IA foque neste documento? (opcional)"
                    value={estado.instrucoes || ''}
                    maxLength={1000}
                    onClick={(e) => e.stopPropagation()}
                    onChange={e => handleAtualizarInstrucoes(indice, e.target.value)}
                  />
                </>
              ) : (
                <>
                  <h3 className="mb-1 text-lg font-semibold text-gray-900">
                    {estado.documentoSugerido.tipo_documento}
                  </h3>
                  {renderizarBadgePrioridade(estado.documentoSugerido.prioridade)}
                  <p className="mt-2 text-sm text-gray-600">
                    <strong>Justificativa:</strong> {estado.documentoSugerido.justificativa}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Barra de Progresso */}
        {renderizarBarraProgresso(estado)}

        {/* Indicador de documento restaurado (quando não temos os arquivos originais) */}
        {estado.status === 'CONCLUIDO' && estado.arquivos.length === 0 && estado.documentoIds.length > 0 && (
          <div className="mt-3 rounded-md bg-green-50 p-3">
            <div className="flex items-center space-x-2">
              <CheckCircle className="size-5 text-green-600" />
              <p className="text-sm text-green-700">
                <strong>Documento enviado anteriormente</strong>
              </p>
            </div>
            <p className="mt-1 text-xs text-green-600">
              Este documento já foi enviado em uma sessão anterior e está disponível no sistema.
            </p>
          </div>
        )}

        {/* Arquivos Enviados (se CONCLUIDO e temos os arquivos originais) */}
        {estado.status === 'CONCLUIDO' && estado.arquivos.length > 0 && (
          <div className="mt-3">
            <p className="mb-2 text-sm font-medium text-gray-700">Arquivos enviados:</p>
            <ul className="space-y-1">
              {estado.arquivos.map((arquivo, idx) => (
                <li key={idx} className="flex items-center justify-between space-x-2 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <FileText className="size-4" />
                    <span>{arquivo.name}</span>
                    <span className="text-xs text-green-600">({estado.statusesArquivos[idx]})</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEstadosDocumentos(estadosAnteriores => {
                        const novosEstados = [...estadosAnteriores];
                        const estadoAtual = novosEstados[indice];
                        const novosArquivos = estadoAtual.arquivos.filter((_, i) => i !== idx);
                        const novosStatusesArquivos = estadoAtual.statusesArquivos.filter((_, i) => i !== idx);
                        const novosProgressosArquivos = estadoAtual.progressoArquivos.filter((_, i) => i !== idx);
                        const novasEtapasArquivos = estadoAtual.etapasArquivos.filter((_, i) => i !== idx);
                        const novosDocumentoIds = estadoAtual.documentoIds.filter((_, i) => i !== idx);
                        const novosUploadIds = estadoAtual.uploadIds.filter((_, i) => i !== idx);
                        
                        let novoStatus: StatusUploadDocumento = 'NAO_ENVIADO';
                        if (novosStatusesArquivos.length > 0) {
                          if (novosStatusesArquivos.every(s => s === 'CONCLUIDO')) {
                            novoStatus = 'CONCLUIDO';
                          } else if (novosStatusesArquivos.some(s => s === 'ERRO')) {
                            novoStatus = 'ERRO';
                          } else {
                            novoStatus = 'PROCESSANDO';
                          }
                        }
                        
                        novosEstados[indice] = {
                          ...estadoAtual,
                          arquivos: novosArquivos,
                          statusesArquivos: novosStatusesArquivos,
                          progressoArquivos: novosProgressosArquivos,
                          etapasArquivos: novasEtapasArquivos,
                          documentoIds: novosDocumentoIds,
                          uploadIds: novosUploadIds,
                          status: novoStatus,
                          progressoPercentual: novosProgressosArquivos.length > 0 ? novosProgressosArquivos.reduce((a, b) => a + b, 0) / novosProgressosArquivos.length : 0
                        };
                        return novosEstados;
                      });
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <XCircle className="size-4" />
                  </button>
                </li>
              ))}
            </ul>
            {/* Hint para drag and drop */}
            <div className="mt-3 rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-center">
              <p className="text-xs text-gray-500">
                <Upload className="mb-1 inline-block size-4" />
                <br />
                Arraste mais arquivos aqui ou clique no card para adicionar
              </p>
            </div>
          </div>
        )}

        {/* Mensagem de Erro */}
        {estado.status === 'ERRO' && estado.mensagemErro && (
          <div className="mt-3 flex items-start space-x-2 rounded-md border border-red-200 bg-red-50 p-3">
            <XCircle className="mt-0.5 size-5 shrink-0 text-red-500" />
            <p className="text-sm text-red-700">{estado.mensagemErro}</p>
          </div>
        )}

        {/* Mensagem de "Não Possuo" */}
        {estado.status === 'MARCADO_NAO_POSSUO' && (
          <>
            <div className="mt-3 flex items-start space-x-2 rounded-md border border-gray-200 bg-gray-50 p-3">
              <AlertCircle className="mt-0.5 size-5 shrink-0 text-gray-500" />
              <p className="text-sm text-gray-700">
                Documento marcado como não disponível
              </p>
            </div>
            <div className="mt-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEstadosDocumentos(estadosAnteriores => {
                    const novosEstados = [...estadosAnteriores];
                    novosEstados[indice] = {
                      ...novosEstados[indice],
                      status: 'NAO_ENVIADO',
                      progressoPercentual: 0,
                      etapaAtual: null,
                      mensagemErro: null
                    };
                    return novosEstados;
                  });
                }}
                className="rounded-md bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
              >
                Alterar decisão
              </button>
            </div>
          </>
        )}

        {/* Campo de Instruções para documentos sugeridos */}
        {!estado.manual && estado.status !== 'MARCADO_NAO_POSSUO' && (
          <div className="mt-4">
            <label htmlFor={`instrucoes-${indice}`} className="mb-1 block text-sm font-medium text-gray-700">
              Instruções para o analista (opcional):
            </label>
            <textarea
              id={`instrucoes-${indice}`}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
              rows={2}
              placeholder="Ex: Documento está incompleto, anexei apenas a primeira página."
              value={estado.instrucoes || ''}
              onClick={(e) => e.stopPropagation()}
              onChange={e => handleAtualizarInstrucoes(indice, e.target.value)}
            />
          </div>
        )}

        {/* Botões de Ação - ÁREA DE DRAG AND DROP */}
        {estado.status === 'NAO_ENVIADO' && (
          <div className="mt-4 space-y-3">
            {/* Área principal de drop */}
            <div className="cursor-pointer rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center transition-colors hover:border-blue-400 hover:bg-blue-50">
              <Upload className={`mx-auto mb-2 size-8 ${isDragActive ? 'animate-bounce text-blue-500' : 'text-gray-400'}`} />
              <p className="font-medium text-gray-700">
                {isDragActive ? 'Solte os arquivos aqui' : 'Arraste arquivos aqui ou clique para selecionar'}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                PDF, DOCX, JPEG, PNG (máx. 20MB cada, múltiplos arquivos permitidos)
              </p>
            </div>

            {/* Botão "Não Possuo" */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleMarcarNaoPossuo(indice);
              }}
              className="w-full rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-200"
            >
              Não Possuo este Documento
            </button>
          </div>
        )}

        {/* Botão "Tentar Novamente" (se erro) */}
        {estado.status === 'ERRO' && (
          <div className="mt-4 space-y-3">
            {/* Área de drop para tentar novamente */}
            <div className="cursor-pointer rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4 text-center transition-colors hover:border-blue-400 hover:bg-blue-50">
              <Upload className="mx-auto mb-2 size-6 text-gray-400" />
              <p className="text-sm font-medium text-gray-700">
                Arraste arquivos ou clique para tentar novamente
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mx-auto w-full max-w-5xl p-6">
      {/* Título e Descrição */}
      <div className="mb-6">
        <h2 className="mb-2 text-2xl font-bold text-gray-900">
          Documentos Sugeridos
        </h2>
        <p className="text-gray-600">
          Baseado na análise do processo, os seguintes documentos são recomendados 
          para uma análise completa. Documentos marcados como <strong>ESSENCIAL</strong> são 
          críticos para a qualidade do resultado.
        </p>
        <p className="mt-2 text-sm text-blue-600">
          💡 <strong>Dica:</strong> Você pode arrastar múltiplos arquivos diretamente para cada caixa de documento!
        </p>
      </div>

      {/* CORREÇÃO CRÍTICA: Seção de Documentos Já Enviados Anteriormente */}
      {documentosJaEnviados && documentosJaEnviados.filter(d => isStatusConcluido(d.status_upload)).length > 0 && (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="mb-3 flex items-center gap-2">
            <CheckCircle className="size-5 text-green-600" />
            <h3 className="font-semibold text-green-800">
              Documentos Enviados Anteriormente ({documentosJaEnviados.filter(d => isStatusConcluido(d.status_upload)).length})
            </h3>
          </div>
          <p className="mb-3 text-sm text-green-700">
            Os seguintes documentos já foram enviados em sessões anteriores e estão disponíveis para a análise:
            Os seguintes documentos já foram enviados em sessões anteriores e estão disponíveis para a análise:
          </p>
          <ul className="space-y-2">
            {documentosJaEnviados
              .filter(doc => isStatusConcluido(doc.status_upload))
              .map((doc, idx) => (
                <li key={doc.documento_id || idx} className="flex items-center gap-2 text-sm text-green-700">
                  <FileText className="size-4" />
                  <span className="font-medium">{doc.nome_arquivo}</span>
                  <span className="rounded-full bg-green-200 px-2 py-0.5 text-xs text-green-800">
                    ✓ Concluído
                  </span>
                </li>
              ))}
          </ul>
        </div>
      )}

      {/* Seção de Arquivos Ignorados (formatos não suportados) */}
      {arquivosIgnorados && arquivosIgnorados.length > 0 && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="mb-3 flex items-center gap-2">
            <XCircle className="size-5 text-red-600" />
            <h3 className="font-semibold text-red-800">
              Arquivos Não Processados ({arquivosIgnorados.length})
            </h3>
          </div>
          <p className="mb-3 text-sm text-red-700">
            Os seguintes arquivos foram ignorados durante o upload porque não são suportados pelo sistema:
          </p>
          <ul className="space-y-2">
            {arquivosIgnorados.map((arquivo, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm text-red-700">
                <AlertCircle className="size-4" />
                <span className="font-medium">{arquivo.nome_arquivo}</span>
                <span className="rounded bg-red-200 px-1.5 py-0.5 text-xs text-red-800">{arquivo.extensao}</span>
                <span className="text-red-600">- {arquivo.motivo}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-red-600">
            💡 Formatos suportados: PDF, DOCX, PNG, JPG, JPEG
          </p>
        </div>
      )}

          <div className="mb-6 flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                setEstadosDocumentos(prev => [
                  ...prev,
                  {
                    documentoSugerido: {
                      tipo_documento: '',
                      prioridade: 'desejavel',
                      justificativa: '',
                      manual: true,
                    },
                    status: 'NAO_ENVIADO',
                    uploadIds: [],
                    documentoIds: [],
                    progressoPercentual: 0,
                    etapaAtual: null,
                    mensagemErro: null,
                    arquivos: [],
                    statusesArquivos: [],
                    progressoArquivos: [],
                    etapasArquivos: [],
                    intervalId: null,
                    instrucoes: '',
                    manual: true,
                  }
                ]);
                setTimeout(() => {
                  const input = document.getElementById('input-tipo-documento-manual') as HTMLInputElement | null;
                  input?.focus();
                }, 100);
              }}
              className="rounded-md bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700"
            >
              Adicionar documento
            </button>
          </div>

      {/* Lista de Documentos Sugeridos */}
      <div className="mb-8 space-y-4">
        {estadosDocumentos.map((estado, indice) => (
          <CardDocumentoComDropzone
            key={indice}
            estado={estado}
            indice={indice}
          />
        ))}
      </div>

      {/* Mensagem de Validação */}
      {!podeAvancar && estadosDocumentos.length > 0 && (
        <div className="mb-6 flex items-start space-x-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <AlertCircle className="mt-0.5 size-5 shrink-0 text-yellow-600" />
          <div className="flex-1">
            <p className="text-sm text-yellow-800">
              <strong>Atenção:</strong> Para avançar, você deve:
            </p>
            <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-yellow-800">
              <li>Enviar ou marcar como "Não Possuo" todos os documentos <strong>ESSENCIAIS</strong></li>
              <li>Enviar pelo menos 1 documento com sucesso</li>
              <li>Aguardar conclusão de todos os uploads em andamento</li>
            </ul>
          </div>
        </div>
      )}

      {/* Botão Avançar */}
      <div className="flex justify-end">
        <button
          onClick={handleAvancar}
          disabled={!podeAvancar}
          className={`rounded-lg px-6 py-3 font-semibold transition-colors ${
            podeAvancar
              ? 'cursor-pointer bg-blue-600 text-white hover:bg-blue-700'
              : 'cursor-not-allowed bg-gray-300 text-gray-500'
          }`}
        >
          Avançar para Seleção de Agentes
        </button>
      </div>
    </div>
  );
}
